/**
 * Find Deal Handler
 * Query handler for finding deals by ID or company
 */

import type { ToolExecutionContext } from '../../types/index.js';
import { QueryHandlerBase } from '../base/index.js';
import { stringField, uuidField } from '../../utils/schema-builder.js';
import { createUserGraphQLClient } from '../../utils/graphql-client.js';

/**
 * Find deal handler
 * Uses GraphQL queries to search for deals with RLS enforcement
 */
export class FindDealHandler extends QueryHandlerBase<any> {
  id = 'find_deal';

  schema = {
    type: 'object',
    properties: {
      id: uuidField('UUID of the deal (optional if company_id is provided)'),
      company_id: uuidField('UUID of the company to find deals for (optional if id is provided)'),
    },
    required: [],
  };

  async execute(params: Record<string, any>, context?: ToolExecutionContext): Promise<any> {
    if (!params.id && !params.company_id) {
      throw new Error('Either id or company_id must be provided');
    }

    if (!context?.userToken) {
      throw new Error('User token is required for deal search');
    }

    try {
      // Dynamically import GraphQL types and documents to avoid circular dependencies
      const { GetDealDocument, GetDealssDocument } = await import('@/src/gql/graphql');
      
      // Create user-scoped GraphQL client that respects RLS
      const client = createUserGraphQLClient(context.userToken);

      if (params.id) {
        // Search by ID using GraphQL
        const result = await client.query({
          query: GetDealDocument,
          variables: {
            id: params.id,
          },
          fetchPolicy: 'network-only',
        });

        if (result.error) {
          throw new Error(`GraphQL error: ${result.error.message}`);
        }

        const data = result.data as any;
        const deal = data?.dealsCollection?.edges?.[0]?.node;
        if (!deal) {
          return {
            error: `Deal with ID "${params.id}" not found`,
            id: params.id,
          };
        }

        return {
          ...deal,
          viewInApp: `[View Deal](/deals/${deal.id})`,
        };
      } else {
        // Search by company_id using GraphQL with filter
        const result = await client.query({
          query: GetDealssDocument,
          variables: {
            first: 10,
            filter: {
              company_id: {
                eq: params.company_id,
              },
            },
          },
          fetchPolicy: 'network-only',
        });

        if (result.error) {
          throw new Error(`GraphQL error: ${result.error.message}`);
        }

        const data = result.data as any;
        const deals = data?.dealsCollection?.edges?.map((edge: any) => edge?.node).filter((node: any) => node != null) || [];

        if (deals.length === 0) {
          return {
            error: `No deals found for company "${params.company_id}"`,
            company_id: params.company_id,
          };
        }

        const viewInApp = (d: any) => `[${d.title || 'Deal'}](/deals/${d.id})`;
        if (deals.length === 1) {
          return { ...deals[0], viewInApp: viewInApp(deals[0]) };
        }
        return {
          matches: deals.map((d: any) => ({ ...d, viewInApp: viewInApp(d) })),
          count: deals.length,
          viewInApp: deals.map(viewInApp).join(' · '),
        };
      }
    } catch (error) {
      throw new Error(`Deal search failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}
